import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Sidebar, SidebarContent, SidebarHeader } from '~/components/sidebar';
import Icon, { ICON_USER_LINE } from '~/components/icon';
import { MainTitle, SubTitle } from '~/components/misc/section-head';
import Loading from '~/components/misc/loading';
import useTranslations from '~/components/translation/use-translations';
import WeSpinTable from '../components/we-spin-table';
import { WE_SPIN_PREVIEW_ROOM_TEXT_KEYS } from '../constants/we-spin-text-keys';
import { selectConfiguration, selectPlayersBoughtIntoSession } from '../ducks/we-spin-selectors';
import { useLocation, useParams } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { useEffect, useState } from 'react';
import { FETCH_GAME_ROOM_PLAYERS, fetchPlayersBoughtIntoSession, fetchProfile } from '../ducks/we-spin-duck';
import { fetchCurrentEvent, setCurrentEventRef } from '../ducks/we-spin-home-game-duck';
import useIntervalAction from '~/hooks/use-interval-action';
import { ROUTE_ACCOUNT_WE_SPIN_DASHBOARD, ROUTE_WE_SPIN_GAME_ROOM_BUY_IN } from '../constants/we-spin-route-constants';
import SidebarScrollAware from '~/utils/sidebar-scroll-aware';
import { LeftSideIconType } from '~/components/sidebar/sidebar-header/sidebar-header';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';

const SIDEBAR_TITLE_SCROLL_THRESHOLD = 214;

const HeadIcon = styled(Icon)`
	color: var(--filter-pills-bg-active);
`;
const WeSpinGameRoomPlayers = () => {
	const params = useParams();
	const dispatch = useDispatch();
	const router = useRouter();
	const location = useLocation();

	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_PREVIEW_ROOM_TEXT_KEYS);

	const { weSpinViewListPollInterval } = useSelector(selectConfiguration);
	const playersBoughtIntoSession = useSelector(selectPlayersBoughtIntoSession(params.streamRef)) || null;
	const transformPath = useContextualPathTransformer();

	const [isLoading, setIsLoading] = useState(false);

	// Init Data
	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchProfile());
		dispatch(setCurrentEventRef(params.streamRef));
		dispatch(fetchCurrentEvent()).finally(() => {
			setIsLoading(false);
		});
		dispatch(fetchPlayersBoughtIntoSession(params.streamRef));
	}, [dispatch, params.streamRef]);

	// Pool Data
	useIntervalAction(
		FETCH_GAME_ROOM_PLAYERS,
		() => {
			dispatch(fetchPlayersBoughtIntoSession(params.streamRef));
		},
		weSpinViewListPollInterval || 5000
	);

	if (isLoadingTranslations) return <Loading />;

	return (
		<Sidebar>
			<SidebarScrollAware>
				{scrollYPosition => (
					<SidebarHeader
						transparent
						leftSideIcon={
							scrollYPosition < SIDEBAR_TITLE_SCROLL_THRESHOLD
								? LeftSideIconType.BACK_ICON
								: LeftSideIconType.BACK_ICON_BG
						}
						onLeftSideButtonClick={() => {
							// TODO (navigation) - use search params or redux for state
							router.push(
								transformPath(
									location.state?.returnLink ||
										`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN}/${params.streamRef}`
								),
								{ state: { returnLink: ROUTE_ACCOUNT_WE_SPIN_DASHBOARD } }
							);
						}}
						className="transparent_title"
						opaqueClassName="page-head--opac"
					/>
				)}
			</SidebarScrollAware>
			<SidebarContent className="u-p1">
				<div className="u-fxb-center-v-h u-mt2 we-spin-dashboard-bubble-icon">
					<HeadIcon type={ICON_USER_LINE} width="56" height="56" />
				</div>
				<div className="u-width-100 u-text-c u-mt1">
					<MainTitle>{translations.WE_SPIN_PREVIEW_ROOM_PLAYERS_LABEL}</MainTitle>
				</div>
				<div className="u-width-100 u-text-c u-mt1">
					<SubTitle>{translations.WE_SPIN_GAME_ROOM_PLAYERS_DESC}</SubTitle>
				</div>
				{isLoading ? (
					<Loading />
				) : playersBoughtIntoSession?.length > 0 ? (
					<WeSpinTable className="transactions-details-table u-mt2">
						<tbody>
							{playersBoughtIntoSession?.map((player, i) => (
								<tr key={player}>
									<td>
										<span className="u-mr1 u-ml1">{i + 1}</span>
										<span>{player}</span>
									</td>
									{/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
									<td className="u-text-r">
										<span />
									</td>
								</tr>
							))}
						</tbody>
					</WeSpinTable>
				) : (
					<div className="u-width-100 u-text-c u-mt1">
						<SubTitle>No Players</SubTitle>
					</div>
				)}
			</SidebarContent>
		</Sidebar>
	);
};

export default WeSpinGameRoomPlayers;
